import React from "react";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuthorizationContext } from "contexts/AuthorizationContext";
import "./orgFilter.css";

// Where the sidebar's organization selector was: the filtered organization,
// which opens it in Access rights; a switch that pauses the filter (every
// organization is shown, the chip greyed out) or resumes it; and a cross that
// clears it. Nothing with no filter. The compact sidebar shows icons only.
const OrgFilterChip = ({ isCompact = false }) => {
  const navigate = useNavigate();
  const { t } = useTranslation("common");
  const { filteredOrganization, pausedOrganization, pauseOrgFilter, resumeOrgFilter, clearOrgFilter } =
    useAuthorizationContext();

  const organization = filteredOrganization ?? pausedOrganization;
  if (!organization) return null;

  const isPaused = !filteredOrganization;
  const openLabel = t("orgFilter.openInAccessRights", { name: organization.name });
  const switchLabel = isPaused
    ? t("orgFilter.resumeFilter", { name: organization.name })
    : t("orgFilter.pauseFilter", { name: organization.name });
  const openInAccessRights = () =>
    navigate(`/std/prmssns?group=${encodeURIComponent(organization.guid)}`);

  return (
    <div
      className={`${isCompact ? "org-filter-chip-compact" : "org-filter-chip"} ${isPaused ? "is-paused" : ""}`}
      data-testid="org-filter-chip"
    >
      <button
        type="button"
        className="org-filter-chip__open"
        title={openLabel}
        aria-label={openLabel}
        onClick={openInAccessRights}
      >
        <i className="fa-solid fa-sitemap org-filter-chip__icon" aria-hidden="true" />
        {!isCompact && <span className="org-filter-chip__name">{organization.name}</span>}
      </button>
      <button
        type="button"
        className="org-filter-chip__switch"
        title={switchLabel}
        aria-label={switchLabel}
        aria-pressed={!isPaused}
        onClick={isPaused ? resumeOrgFilter : pauseOrgFilter}
      >
        <i className={`fas ${isPaused ? "fa-toggle-off" : "fa-toggle-on"}`} aria-hidden="true" />
      </button>
      <button
        type="button"
        className="org-filter-chip__clear"
        title={t("orgFilter.clearFilter")}
        aria-label={t("orgFilter.clearFilter")}
        onClick={clearOrgFilter}
      >
        <i className="fas fa-times" aria-hidden="true" />
      </button>
    </div>
  );
};

OrgFilterChip.propTypes = {
  isCompact: PropTypes.bool,
};

export default OrgFilterChip;
